/**
 * Admin Portfolios — V2.N.6
 *
 * Vue agrégée des portfolios mirrorés à travers comptes et devices :
 *   - Stats globales (nb portfolios, moy. tokens/positions)
 *   - Top tokens suivis (combien de holders)
 *
 * Lit /api/admin/devices. Style Stripe-inspired light V2.L.1.
 */

import { useEffect, useState } from 'react';
import { fetchDevices } from '../lib/fetchAdminAction';
import { adminPageStyle, adminCardStyle } from '../lib/adminStyles';
import '../layout/AdminLayout.css';

interface Stats {
  portfolios: number;
  devices_with_portfolio: number;
  avg_tokens: number;
  avg_positions: number;
  total_tokens: number;
  total_positions: number;
}
interface TokenRow { coin_id: string; symbol?: string; holders: number; }

export function Portfolios() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [topTokens, setTopTokens] = useState<TokenRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const [s, t] = await Promise.all([
        fetchDevices<Stats>('portfolio_stats'),
        fetchDevices<TokenRow[]>('top_tokens', { limit: 40 }),
      ]);
      if (!cancelled) {
        setStats(s);
        setTopTokens(t || []);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const maxHolders = topTokens.length ? topTokens[0].holders : 1;

  return (
    <main style={adminPageStyle}>
      <header style={{ marginBottom: '20px' }}>
        <h1 style={{ fontSize: '24px', fontWeight: 700, margin: 0 }}>Portfolios</h1>
        <div style={{ fontSize: '13px', color: 'var(--muted)', marginTop: '4px' }}>
          Portfolios et holdings mirrorés à travers comptes et appareils anonymes.
        </div>
      </header>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '20px' }}>
        <StatCard label="Portfolios" value={stats?.portfolios ?? '—'} accent="#0969da" />
        <StatCard label="Appareils" value={stats?.devices_with_portfolio ?? '—'} accent="#0e9f6e" sub="Avec au moins 1 portfolio" />
        <StatCard label="Moy. tokens" value={stats?.avg_tokens ?? '—'} accent="#6366f1" sub="Par portfolio" />
        <StatCard label="Moy. positions" value={stats?.avg_positions ?? '—'} accent="#8b5cf6" sub="Par portfolio" />
        <StatCard label="Total positions" value={stats?.total_positions ?? '—'} accent="#d97706" />
      </div>

      <section style={adminCardStyle}>
        <h2 style={{ fontSize: '15px', fontWeight: 700, margin: 0, marginBottom: '12px' }}>Top tokens suivis</h2>
        {loading ? (
          <div style={{ fontSize: '13px', color: 'var(--muted)' }}>Chargement…</div>
        ) : topTokens.length === 0 ? (
          <div style={{ fontSize: '12px', color: 'var(--muted)', fontStyle: 'italic' }}>
            Aucun portfolio mirrored. (Les données apparaissent après le sync V2.N.4.)
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {topTokens.map((t) => (
              <div key={t.coin_id} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '120px', fontSize: '12px', fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {t.symbol || t.coin_id}
                </div>
                <div style={{ flex: 1, height: '18px', background: 'rgba(9,105,218,0.08)', borderRadius: '4px', overflow: 'hidden' }}>
                  <div style={{ width: `${(t.holders / maxHolders) * 100}%`, height: '100%', background: 'linear-gradient(90deg, #0969da, #4f46e5)' }} />
                </div>
                <div style={{ width: '50px', textAlign: 'right', fontSize: '12px', fontVariantNumeric: 'tabular-nums', color: 'var(--muted)' }}>{t.holders}</div>
              </div>
            ))}
          </div>
        )}
      </section>
    </main>
  );
}

function StatCard({ label, value, sub, accent }: { label: string; value: number | string; sub?: string; accent: string }) {
  return (
    <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: '10px', padding: '14px' }}>
      <div style={{ fontSize: '11px', color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: '26px', fontWeight: 700, marginTop: '4px', color: accent, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      {sub && <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '2px' }}>{sub}</div>}
    </div>
  );
}

